<template>
  <div class="deal-cart floating-pane fading overall-summary">
    <div class="floating-pane-header">
      <icon-deal />
      <span>{{ capitalize($t('names.selectedDeal', 2)) }}</span>
    </div>
    <div class="floating-pane-content p-3">
      <div class="deals">
        <div
          v-for="p in cart"
          :key="p.id"
          class="badge mb-3 mr-3 w-100 cart-item">
          <div
            class="flex-h py-2 justify-content-between align-content-center align-items-center"
            style="gap: 2rem">
            <span
              class="text-truncate py-1 m-0 text-sm"
              style="color: black">
              {{ p.name }}
            </span>
            <div
              class="d-flex flex-row align-items-center"
              style="gap: 0.25rem">
              <button
                v-if="previewItems"
                class="item-actions"
                @click="emit('previewItem', p)">
                <component
                  :is="activeItem === p ? IconHide : IconView"
                  class="eye-icon" />
              </button>
              <button
                class="item-actions"
                @click="emit('removeDeal', p)">
                <icon-x style="fill: gray" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="warningMessages.length > 0"
        class="d-flex flex-column alert alert-warning align-items-start mt-2">
        <div class="icon-xs d-flex align-items-center mb-2">
          <icon-warning-hexagon />
          <span class="ml-2 font-weight-bold">{{ capitalize(i18n.t('messages.selectionRestrictions')) }}</span>
        </div>
        <ul class="mb-1">
          <li
            v-for="message in warningMessages"
            :key="message">
            {{ capitalize(message) }}
          </li>
        </ul>
      </div>
      <hr />
      <div class="actions flex-h justify-content-center mt-3">
        <button
          id="confirm_cart_button"
          class="btn btn-secondary-hollow btn-xs"
          name="confirmCartSelection"
          :disabled="disabled"
          @click="emit('confirmCart')">
          <icon-add />
          {{ confirmButtonLabel }}
        </button>
        <button
          id="clear_selection_button"
          class="btn btn-default btn-xs ml-3"
          name="clearCartSelection"
          @click="emit('clearCart')">
          <icon-delete />
          {{ capitalize($t('actions.clear')) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, toRefs } from 'vue';
  import { useI18n } from 'vue-i18n';

  import {
    IconAdd,
    IconDeal,
    IconDelete,
    IconHide,
    IconView,
    IconWarningHexagon,
    IconX,
  } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';

  interface IProps {
    cart: Package[];
    disabled?: boolean;
    previewItems?: boolean;
    activeItem?: Package;
    confirmButtonLabel: string;
    warningMessages?: string[];
  }

  const props = withDefaults(defineProps<IProps>(), {
    disabled: false,
    previewItems: false,
    activeItem: null,
    warningMessages: () => [],
  });

  const { cart, disabled, warningMessages } = toRefs(props);

  const emit = defineEmits(['clearCart', 'removeDeal', 'confirmCart', 'previewItem']);

  const i18n = useI18n();
</script>

<style scoped lang="scss">
  @use '@/styles/variables.scss' as *;

  .item-actions {
    flex: 1 1 0;
    align-self: center;
    background-color: transparent;
    border: none;
    transition: 0.3s ease;
    outline: none;
    svg {
      margin: 0;
    }
    :hover {
      cursor: pointer;
      filter: brightness(75%);
    }
  }

  .deals {
    ::v-deep(.badge) {
      text-align: start;
      white-space: inherit;
    }
  }

  .cart-item {
    background: var(--paneHeaderBackgroundColor);
    border: 1px solid var(--tableHeaderBackgroundColor);
  }

  .eye-icon {
    stroke: var(--info);
  }
</style>
